import { Button, Card, Checkbox, Col, Divider, Flex, Form, FormProps, Radio, Row, Skeleton, Spin, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DatePickerElement from 'components/datePicker/datePicker';
import DisabledElement from 'components/disabledElement/disabledElement';
import ActionCostComponent from '../../cost';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { onCalculateTotalAmount, onCheckEligibilityDate, onCreateDividend } from '../services/dividends';
import NotificationService from 'core/services/notification';
import dayjs from 'dayjs';
import NumberElement from 'components/number/number';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import { useDebouncedCallback } from 'use-debounce';
import VerificationModal from '../../verificationModal';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { CreateDividendPayload, Dividend } from '../models/dividends';
import AbsteensComponent from '../components/absteensComponents';
import DirectIbanComponent from '../components/directIbanComponents';

// services

// styles

// models

interface DividendCreatePageProps {}

const DividendCreatePage: FunctionComponent<DividendCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [createForm] = Form.useForm();

	const eligibilityDateWatcher = Form.useWatch('eligibility_date', createForm);
	const revenuePerShareWatcher = Form.useWatch('revenue_per_share', createForm);
	const forbiddenExceptedShareholdersWatcher: Dividend['forbidden_excepted_shareholders'] = Form.useWatch(
		'forbidden_excepted_shareholders',
		createForm,
	);
	const directTransferExceptedShareholdersWatcher: Dividend['direct_transfer_excepted_shareholders'] = Form.useWatch(
		'direct_transfer_excepted_shareholders',
		createForm,
	);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const [isCheckingEligibilityDate, setIsCheckingEligibilityDate] = useState<boolean>(false);
	const [isValidEligibilityDate, setIsValidEligibilityDate] = useState<boolean>(false);

	const [isCalculatingTotalAmount, setIsCalculatingTotalAmount] = useState<boolean>(false);
	const [totalAmount, setTotalAmount] = useState<string>('0');

	const [acknowledge1, setAcknowledge1] = useState<boolean>(false);
	const [acknowledge2, setAcknowledge2] = useState<boolean>(false);

	useEffect(() => {
		getServiceCost();
	}, []);

	useEffect(() => {
		calculateTotalAmount();
	}, [forbiddenExceptedShareholdersWatcher]);

	const getServiceCost = async () => {
		try {
			setIsLoading(true);

			const response = await onGetServiceCost('dividends');
			setServiceCost(response?.success);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const checkEligibilityDate = async (date: string) => {
		try {
			if (!date) return;

			setIsCheckingEligibilityDate(true);

			const response = await onCheckEligibilityDate(date);
			setIsValidEligibilityDate(response.isValidEligibilityDate);

			createForm.resetFields(['distribution_date', 'revenue_per_share', 'forbidden_excepted_shareholders', 'direct_transfer_excepted_shareholders']);
			setTotalAmount('0');

			setIsCheckingEligibilityDate(false);
		} catch (error: any) {
			setIsValidEligibilityDate(false);
			setIsCheckingEligibilityDate(false);

			if (error?.response?.data?.data?.errors?.eligibility_date?.[0]) {
				createForm.setFields([
					{
						name: 'eligibility_date',
						errors: [error?.response?.data?.data?.errors?.eligibility_date?.[0]],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const calculateTotalAmount = async () => {
		try {
			const date = createForm.getFieldValue('eligibility_date');
			const revenue_per_share = createForm.getFieldValue('revenue_per_share');

			if (!date || !revenue_per_share) return;

			setIsCalculatingTotalAmount(true);

			const response = await onCalculateTotalAmount(
				dayjs(date).format('YYYY-MM-DD'),
				revenue_per_share,
				forbiddenExceptedShareholdersWatcher?.map((item) => item.id) || [],
				directTransferExceptedShareholdersWatcher?.map((item) => item.id) || [],
			);
			setTotalAmount(response.totalAmount);

			createForm.setFieldsValue({
				forbidden_excepted_shareholders: response.forbidden_excepted_shareholders,
				direct_transfer_excepted_shareholders: response.direct_transfer_excepted_shareholders,
			});

			setIsCalculatingTotalAmount(false);
		} catch (error: any) {
			setIsCalculatingTotalAmount(false);

			if (error?.response?.data?.data?.errors?.eligibility_date?.[0]) {
				createForm.setFields([
					{
						name: 'eligibility_date',
						errors: [error?.response?.data?.data?.errors?.eligibility_date?.[0]],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const calculateTotalAmountDebounced = useDebouncedCallback(calculateTotalAmount, 1000);

	const onSubmit = async (otp: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload: CreateDividendPayload = {
				otp: otp.key,
				interval_of_distribution: createForm.getFieldValue('interval_of_distribution'),
				revenue_per_share: createForm.getFieldValue('revenue_per_share').toString(),
				eligibility_date: dayjs(createForm.getFieldValue('eligibility_date')).format('YYYY-MM-DD'),
				distribution_date: dayjs(createForm.getFieldValue('distribution_date')).format('YYYY-MM-DD'),
				forbidden_excepted_shareholders: createForm.getFieldValue('forbidden_excepted_shareholders')?.map((item: any) => item.id) || [],
				direct_transfer_excepted_shareholders: createForm.getFieldValue('direct_transfer_excepted_shareholders')?.map((item: any) => item.id) || [],
			};

			await onCreateDividend(payload);

			navigate('/dashboard/actions/dividends');
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DIVIDENDS_CREATED'));
		} catch (error: any) {
			setIsSubmitting(false);

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');

			NotificationService.error(t('COMMON.ERROR'), message);

			mapResponseErrors(error, createForm);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.DIVIDENDS')} subtitle={t('ACTIONS.FILL_TO_BUILD')} />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<Form form={createForm} initialValues={{ interval_of_distribution: 'Interim', revenue_per_share: 0 }}>
						<h6 className="fw-medium mt-4">{t('ACTIONS.DISTRIBUTION_INTERVAL')}</h6>
						<Form.Item name="interval_of_distribution" rules={[{ required: true, message: t('COMMON.REQUIRED_FIELD') }]}>
							<Radio.Group
								className="customRadio"
								style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
								options={[
									{ label: t('ACTIONS.INTERIM_DIVIDENDS'), value: 'Interim' },
									{ label: t('ACTIONS.ANNUAL_DIVIDENDS'), value: 'Annual' },
								]}
							/>
						</Form.Item>

						<Divider />

						<Row gutter={[16, 16]}>
							<Col span={24} md={12} lg={6}>
								<DatePickerElement
									name="eligibility_date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.ELIGIBILITY_DATE')}
									isLoading={isCheckingEligibilityDate}
									onChange={(date) => checkEligibilityDate(date?.format('YYYY-MM-DD') || '')}
									maxDate={dayjs().subtract(1, 'day')}
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								<DatePickerElement
									name="distribution_date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.DISTRIBUTION_DATE')}
									disabled={!isValidEligibilityDate}
									minDate={dayjs().add(3, 'day')}
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								<FormNumberElement
									name="revenue_per_share"
									disabled={!isValidEligibilityDate}
									label={t('ACTIONS.REVENUE_PER_SHARE')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											validator: (_, value) => {
												if (value === null || value === undefined || value <= 0) {
													return Promise.reject(new Error(t('COMMON.FIELD_REQUIRED')));
												}
												return Promise.resolve();
											},
										},
									]}
									precision={2}
									onChange={calculateTotalAmountDebounced}
									showPrice
								/>
							</Col>
							<Col span={24} md={12} lg={6} style={{ filter: isValidEligibilityDate ? '' : 'grayscale(1)opacity(0.5)' }}>
								{isCalculatingTotalAmount ? (
									<>
										<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'block' }}>
											{t('ACTIONS.TOTAL_AMOUNT_OF_DIVIDENDS')}
										</Typography.Text>
										<Skeleton.Button active className="w-100" style={{ height: 40, maxWidth: 'var(--inputWidth)', borderRadius: 'var(--radius)' }} />
									</>
								) : (
									<DisabledElement value={totalAmount} showPrice label={t('ACTIONS.TOTAL_AMOUNT_OF_DIVIDENDS')} />
								)}
							</Col>
						</Row>

						<AbsteensComponent
							eligibility_date={eligibilityDateWatcher}
							revenue_per_share={revenuePerShareWatcher}
							form={createForm}
							disabled={!isValidEligibilityDate}
						/>

						<DirectIbanComponent
							eligibility_date={eligibilityDateWatcher}
							revenue_per_share={revenuePerShareWatcher}
							form={createForm}
							disabled={!isValidEligibilityDate}
						/>

						<Divider />

						<Flex justify="space-between" align="center" gap={20} wrap>
							<div>
								<div className="mb-3">
									<Checkbox onChange={(event) => setAcknowledge1(event.target.checked)}>
										<span className="fw-semibold d-block">{t('ACTIONS.TERMS_AND_CONDITIONS')}</span>
										<span className="text-light">{t('ACTIONS.AGREE_TERMS_AND_CONDITIONS')}</span>
									</Checkbox>
								</div>
								<div>
									<Checkbox onChange={(event) => setAcknowledge2(event.target.checked)}>
										<span className="fw-semibold d-block">{t('ACTIONS.THREE_DAYS_PAYMENT')}</span>
										<span className="text-light">{t('ACTIONS.FAILURE_TO_PAY_SDAD_BILL')}</span>
									</Checkbox>
								</div>
							</div>

							<Flex justify="flex-end" align="center" gap={10}>
								<ActionCostComponent serviceCost={serviceCost!} />

								<Button
									type="primary"
									disabled={!(acknowledge1 && acknowledge2)}
									onClick={async () => {
										try {
											await createForm.validateFields();

											if (
												createForm.getFieldValue('direct_transfer_excepted_shareholders') &&
												createForm.getFieldValue('direct_transfer_excepted_shareholders').findIndex((item: any) => !item.iban) > -1
											) {
												NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ENTER_IBAN_FOR_DIRECT_TRANSFER'));
												return;
											}

											setVerificationModalOpen(true);
										} catch (error) {}
									}}>
									{t('ACTIONS.GENERATE_BILL')}
								</Button>
							</Flex>
						</Flex>
					</Form>
				)}
			</Card>

			<VerificationModal
				title={t('ACTIONS.CONFIRM_DIVIDEND_CREATION')}
				subTitle={t('ACTIONS.PLEASE_CONFIRM_YOUR_REQUEST')}
				otpOnly
				open={verificationModalOpen}
				isLoading={isSubmitting}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module="dividends_create"
				note={t('ACTIONS.DIVIDENDS_NOTE')}
				noteType="red"
			/>
		</>
	);
};

export default DividendCreatePage;
